/**
 * A tenant's visual identity, derived from nothing but its slug.
 *
 * The login screen renders *before* anyone has authenticated, so the tenant is
 * never known from a session there — only from a deep link (`?tenant=`) or from
 * what this browser remembers of the last successful sign-in. Everything in
 * here is therefore a pure function of a string that may well be null, and the
 * null case has to look deliberate rather than broken.
 *
 * The palette stays fixed. Bauhaus worked from a set primary palette and varied
 * the geometry; a per-tenant hue would drift off-brand by the third customer
 * and still be harder to tell apart than a different shape in a different
 * corner. So the slug decides the composition and which of the three brand
 * tones leads — never the colours themselves.
 */

/**
 * The three tones a composition may draw from. The stylesheet maps each name
 * to a design token (`[data-tone]` in admin.css); the data never carries a
 * colour, so both themes fall out of one composition.
 */
export const TONES = ['ink', 'signal', 'yellow'];

/**
 * FNV-1a, 32-bit. Stable across browsers and runtimes, needs no dependency,
 * and spreads short similar strings ("clahra" / "clahra-dev") far apart —
 * which a naive character sum would not.
 */
export function hashSlug(slug) {
  let h = 0x811c9dc5;
  for (let i = 0; i < slug.length; i += 1) {
    h ^= slug.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/** mulberry32 — a small deterministic PRNG so one seed yields one composition. */
export function rng(seed) {
  let a = seed >>> 0;
  return function next() {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const pick = (next, list) => list[Math.floor(next() * list.length)];
const between = (next, lo, hi) => lo + next() * (hi - lo);
const snap = (value, step) => Math.round(value / step) * step;

/**
 * Build the composition.
 *
 * The field is the viewport, not a 100×100 square. Every shape is pinned to an
 * `anchor` given in percent of the viewport and then drawn in CSS pixels around
 * that point: a disc of radius 280 is 280 px on a laptop and on a 4K display
 * alike — only *where* it hangs moves with the screen. (Scaling the drawing
 * with the window, as a viewBox does, turned a modest arc into a 600-px slab on
 * a wide monitor. A repeating `<pattern>` keeps the size but makes wallpaper,
 * and a regular grid is the one thing a Bauhaus composition never is.)
 *
 * The plan is a poster's: one mass bleeding in from a side edge, a smaller
 * counter-form on the far edge, a bar that pierces the mass, one hairline
 * across the whole width, and one small solid square — the full stop of the
 * wordmark — as the only element allowed to read at full strength. The centre
 * stays empty because the card lives there.
 *
 * Angles are constructed, never arbitrary: multiples of 45° only. A shape at
 * 37° reads as a rendering slip; at 45° it reads as a decision.
 */
function compose(next, accent, side) {
  const rest = TONES.filter((t) => t !== accent);
  // "Inward" from the edge the mass hangs on, and that edge's x in percent.
  const mx = side === 'left' ? 1 : -1;
  const edge = side === 'left' ? 0 : 100;
  const far = 100 - edge;
  const shapes = [];

  // -- 1. The mass ------------------------------------------------------------
  // The one element that carries the silhouette. Disc, half-disc or monolith;
  // always partly off-screen so the field reads as a crop of something larger.
  const massKind = pick(next, ['disc', 'arc', 'slab']);
  const massY = pick(next, [30, 50, 70]);
  const mass = { kind: massKind, tone: accent, weight: 'mass', anchor: [edge, massY], cx: 0, cy: 0 };
  if (massKind === 'disc') {
    mass.r = snap(between(next, 240, 320), 8);
    mass.cx = mx * snap(mass.r * between(next, 0.3, 0.7), 8);
  } else if (massKind === 'arc') {
    // Flat side towards the edge, bulge into the field. Either flush with the
    // edge or clearly stepped in from it — a 16-px sliver of ground between
    // the two reads as a rendering fault, not a choice.
    mass.r = snap(between(next, 260, 360), 8);
    mass.cx = mx * pick(next, [0, 0, 80]);
    mass.angle = side === 'left' ? 0 : 180;
  } else {
    mass.len = snap(between(next, 520, 760), 8);
    mass.thick = snap(between(next, 72, 140), 4);
    mass.cx = mx * snap(between(next, 96, 220), 8);
    // Mostly standing; every third one leans.
    mass.angle = pick(next, [90, 90, 45, 135]);
  }
  shapes.push(mass);

  // -- 2. The counter-form ----------------------------------------------------
  // Smaller, on the far edge, never the same primitive as the mass. Without it
  // the composition tips over to one side.
  const counterKind = pick(next, ['disc', 'ring', 'arc', 'slab'].filter((k) => k !== massKind));
  const counterTone = pick(next, rest);
  const counterY = pick(next, [25, 50, 75]);
  const counter = { kind: counterKind, tone: counterTone, weight: 'mass', anchor: [far, counterY], cx: 0, cy: 0 };
  if (counterKind === 'disc') {
    counter.r = snap(between(next, 96, 170), 4);
    counter.cx = -mx * snap(counter.r * between(next, 0.2, 0.7), 4);
  } else if (counterKind === 'ring') {
    counter.weight = 'line';
    counter.r = snap(between(next, 110, 190), 4);
    counter.thick = pick(next, [2, 3, 4]);
    counter.cx = -mx * snap(between(next, 120, 260), 4);
  } else if (counterKind === 'arc') {
    // A dome rising from the bottom edge, or a bowl hanging from the top — the
    // sun of every second Bauhaus poster.
    const top = next() < 0.5;
    counter.r = snap(between(next, 140, 220), 4);
    counter.anchor = [far, top ? 0 : 100];
    counter.cx = -mx * snap(between(next, 160, 340), 8);
    counter.angle = top ? 90 : 270;
  } else {
    counter.len = snap(between(next, 320, 520), 8);
    counter.thick = snap(between(next, 28, 56), 4);
    counter.cx = -mx * snap(between(next, 80, 200), 4);
    counter.angle = next() < 0.5 ? 90 : 0;
  }
  shapes.push(counter);

  // -- 3. The bar that pierces the mass ---------------------------------------
  // Solid, in the tone the mass is not: against a hatched field one full
  // stroke is what makes the print. It always enters from an edge — a bar that
  // starts and stops in open field reads as a slash, not as a cut.
  const barAngle = pick(next, [90, 0, 45, 135]);
  const barLen = snap(between(next, 480, 720), 8);
  const bar = {
    kind: 'slab',
    tone: accent === 'ink' ? 'signal' : 'ink',
    weight: 'line',
    anchor: [edge, massY],
    len: barLen,
    thick: pick(next, [4, 6, 8]),
    angle: barAngle,
    cx: 0,
    cy: 0,
  };
  // How far past the edge the bar begins, and how far it drifts off the line
  // through the mass's centre.
  const overshoot = snap(between(next, 16, 64), 4);
  const drift = snap(between(next, -120, 120), 8);
  if (barAngle === 0) {
    // Horizontal: in from the mass's own edge, past the mass.
    bar.cx = mx * (barLen / 2 - overshoot);
    bar.cy = drift;
  } else if (barAngle === 90) {
    // Vertical: down from the top or up from the bottom, whichever is nearer
    // the mass, a hand's width in from the side.
    const fromTop = massY <= 50;
    bar.anchor = [edge, fromTop ? 0 : 100];
    bar.cx = mx * (160 + Math.abs(drift));
    bar.cy = (fromTop ? 1 : -1) * (barLen / 2 - overshoot);
  } else {
    // Diagonal: in from the side edge at the mass's height, leaning down or
    // up as the angle says (45° leans down when read from the left edge).
    const down = barAngle === (mx > 0 ? 45 : 135);
    const reach = Math.SQRT1_2 * (barLen / 2 - overshoot);
    bar.cx = mx * reach;
    bar.cy = (down ? 1 : -1) * reach + drift;
  }
  shapes.push(bar);

  // -- 4. One hairline across the whole width ---------------------------------
  // Ties the two edges together; kept clear of the card's vertical band.
  const ruleTop = next() < 0.5;
  shapes.push({
    kind: 'rule',
    tone: 'ink',
    weight: 'line',
    anchor: [0, snap(between(next, ruleTop ? 8 : 84, ruleTop ? 16 : 92), 1)],
    thick: 1,
  });

  // -- 5. The mark ------------------------------------------------------------
  // One small solid square, echoing the wordmark's full stop. The only element
  // that may read at full strength, so there is exactly one of it.
  const mark = {
    kind: 'square',
    tone: accent === 'ink' ? 'signal' : accent,
    weight: 'mark',
    size: pick(next, [14, 18, 22]),
  };
  if (massKind !== 'slab' && next() < 0.5) {
    // Set into the mass, off centre — a red dot in a grey circle.
    mark.anchor = [edge, massY];
    mark.x = mass.cx + mx * snap(mass.r * between(next, 0.1, 0.5), 4);
    mark.y = snap(mass.r * between(next, -0.5, 0.2), 4);
  } else {
    // Set beside the counter-form, just inward of it. A stamp in an empty
    // corner reads as a stray pixel; next to a shape it reads as a signature.
    const gap = snap(between(next, 16, 40), 4);
    const lift = between(next, 0.2, 0.5);
    // From the far edge into the field.
    const inward = -mx;
    mark.anchor = counter.anchor;
    // `nearest` is the square's edge nearest the counter-form; it extends
    // inward from there.
    const fromEdge = (nearest) => (inward > 0 ? nearest : nearest - mark.size);
    if (counterKind === 'disc' || counterKind === 'ring') {
      mark.x = fromEdge(counter.cx + inward * (counter.r + gap));
      mark.y = Math.round(-counter.r * lift - mark.size / 2);
    } else if (counterKind === 'arc') {
      // Off the apex of the dome, or under the bowl.
      const top = counter.anchor[1] === 0;
      mark.x = fromEdge(counter.cx + inward * snap(counter.r * lift, 4));
      mark.y = top ? counter.r + gap : -(counter.r + gap) - mark.size;
    } else if (counter.angle === 90) {
      // Past the standing slab's end that faces the middle of the field.
      const above = counter.anchor[1] > 50;
      mark.x = counter.cx - mark.size / 2;
      mark.y = above ? -(counter.len / 2 + gap) - mark.size : counter.len / 2 + gap;
    } else {
      // Past the lying slab's inward end.
      mark.x = fromEdge(counter.cx + inward * (counter.len / 2 + gap));
      mark.y = -mark.size / 2;
    }
  }
  shapes.push(mark);

  // -- 6. A small lattice, some of the time -----------------------------------
  // A few dots in the corner the mass leaves free on its own side. Small enough
  // to be a texture, not a grid.
  if (next() < 0.55) {
    const top = massY > 50;
    const cols = pick(next, [3, 4, 5]);
    const rows = pick(next, [3, 4]);
    const gap = pick(next, [12, 16, 20]);
    const x0 = snap(between(next, 48, 160), 4);
    const y0 = snap(between(next, 56, 150), 4);
    shapes.push({
      kind: 'dots',
      tone: 'ink',
      weight: 'line',
      anchor: [edge, top ? 0 : 100],
      x: mx > 0 ? x0 : -(x0 + (cols - 1) * gap),
      y: top ? y0 : -(y0 + (rows - 1) * gap),
      cols,
      rows,
      gap,
      r: 2,
    });
  }

  return shapes;
}

/** The hatch angles a hand may choose. Never 0° or 90°: ruled, not cut. */
export const HATCH_ANGLES = [20, 35, 50, 65, 115, 130, 145, 160];

/**
 * The tenant's hand: how its plate is cut.
 *
 * This is where tenants differ most visibly. Two tenants may well share a
 * disc in the same corner; they will not share a hatch angle, a line spacing
 * and a cross-hatch at once, and the angle alone is read from across the room.
 * Drawn from its own seed stream so that the composition above stays put when
 * the etching parameters are tuned — and vice versa.
 */
function hand(seed) {
  const next = rng((seed ^ 0x9e3779b9) >>> 0);
  return {
    angle: pick(next, HATCH_ANGLES),
    spacing: snap(between(next, 5, 11), 1),
    // 0 means single hatching; otherwise the second set's angle off the first.
    cross: pick(next, [0, 0, 90, 60, 45]),
    weight: pick(next, [0.7, 1, 1.4]),
    reach: pick(next, [0.4, 0.8, 1.2]),
    // Whether the counter-form is hatched too or laid down as aquatint grain.
    counter: pick(next, ['hatch', 'grain', 'grain']),
  };
}

/**
 * How each shape is worked on the plate. The mass is always hatched — it is
 * the one place the hand shows at full size. The counter-form is the second
 * technique the hand knows. Rings are cut in several turns. The bar and the
 * mark stay solid: one full stroke and one stamp against a field of lines is
 * the contrast that makes a print read as a print.
 */
function technique(shape, counterTechnique) {
  switch (shape.kind) {
    case 'disc':
    case 'arc':
      return shape.weight === 'mass' && shape.role === 'counter' ? counterTechnique : 'hatch';
    case 'slab':
      if (shape.weight === 'line') return 'solid';
      return shape.role === 'counter' ? counterTechnique : 'hatch';
    case 'ring':
      return 'passes';
    case 'square':
      return 'solid';
    default:
      return 'plain';
  }
}

/**
 * @param {string|null|undefined} slug
 * @returns {{slug: string, accent: string, seed: number, side: 'left'|'right',
 *            hatch: object, shapes: Array<object>}|null} null when no tenant is
 *            known — the caller then shows the neutral Etch identity rather
 *            than inventing a tenant that nobody is in.
 */
export function tenantIdentity(slug) {
  const clean = typeof slug === 'string' ? slug.trim().toLowerCase() : '';
  if (!clean) return null;

  const seed = hashSlug(clean);
  const next = rng(seed);
  const accent = pick(next, TONES);
  const side = next() < 0.5 ? 'left' : 'right';
  const hatch = hand(seed);

  // The first two shapes are always mass and counter-form, in that order.
  const shapes = compose(next, accent, side).map((shape, i) => {
    const role = i === 0 ? 'mass' : i === 1 ? 'counter' : undefined;
    const withRole = role ? { ...shape, role } : shape;
    return { ...withRole, technique: technique(withRole, hatch.counter) };
  });

  return {
    slug: clean,
    accent,
    seed,
    side,
    hatch,
    shapes,
  };
}

/**
 * The field when no tenant is known.
 *
 * Hand-set rather than hashed: the neutral screen is Etch itself, and Etch is
 * ink on paper — no yellow, nothing left to chance. Same grammar as a tenant's
 * plate (a hatched mass, a counter-form, a solid bar in from an edge, one
 * rule, one stamp) so that the tenant screens read as variations of it rather
 * than as a different design. The stamp keeps the wordmark's red: that dot is
 * the brand, not a tenant.
 */
export function neutralIdentity() {
  return {
    slug: null,
    accent: 'ink',
    seed: 0,
    side: 'left',
    hatch: { angle: 65, spacing: 8, cross: 0, weight: 1, reach: 0.6, counter: 'hatch' },
    shapes: [
      { kind: 'arc', tone: 'ink', weight: 'mass', role: 'mass', technique: 'hatch', anchor: [0, 50], cx: 0, cy: 0, r: 300, angle: 0 },
      { kind: 'ring', tone: 'ink', weight: 'line', role: 'counter', technique: 'passes', anchor: [100, 30], cx: -200, cy: 0, r: 140, thick: 2 },
      // Down from the top edge, through the half-disc.
      { kind: 'slab', tone: 'ink', weight: 'line', technique: 'solid', anchor: [0, 0], cx: 220, cy: 260, len: 600, thick: 6, angle: 90 },
      { kind: 'rule', tone: 'ink', weight: 'line', technique: 'plain', anchor: [0, 88], thick: 1 },
      // Just inward of the ring, a little above its centre.
      { kind: 'square', tone: 'signal', weight: 'mark', technique: 'solid', anchor: [100, 30], x: -386, y: -65, size: 18 },
    ],
  };
}

/**
 * Human-facing label for a slug, used until the real tenant name is known.
 * `nanny-angel` → `Nanny Angel`.
 */
export function tenantLabel(slug) {
  const clean = typeof slug === 'string' ? slug.trim() : '';
  if (!clean) return '';
  return clean
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/**
 * Which tenant this visit belongs to — decided before anyone has signed in, so
 * it can only come from the link that was followed or from what this browser
 * remembers of the last successful sign-in here.
 *
 * The slug is validated rather than trusted: it arrives from the query string
 * and ends up as a visible label, and a bound on shape and length keeps a
 * 10 kB "tenant" out of the card. React escapes the value either way; this is
 * about the screen staying sane, not about injection.
 */
export function resolveTenantSlug(search, storage) {
  const shaped = /^[a-z0-9][a-z0-9-]{1,59}$/i;

  const fromUrl = new URLSearchParams(search || '').get('tenant');
  if (fromUrl && shaped.test(fromUrl)) return fromUrl.toLowerCase();

  try {
    const remembered = storage?.getItem('etch.tenant');
    if (remembered && shaped.test(remembered)) return remembered.toLowerCase();
  } catch {
    // Private mode, blocked storage, embedded webview — a missing memory is a
    // neutral screen, never a broken one.
  }
  return null;
}
